/**
 * P0.9 - XHR-BASED API CLIENT (BYPASSES FETCH INTERCEPTOR)
 * 
 * The Emergent preview logger (emergent-main.js) intercepts window.fetch
 * and consumes response.body on non-OK responses before returning.
 * This corrupts the response for our code.
 * 
 * Solution: Use XMLHttpRequest which is NOT intercepted.
 * 
 * Contract:
 * - Uses XMLHttpRequest, NOT fetch
 * - Returns parsed result { ok, status, traceId, json, rawText, error }
 * - NEVER returns a Response object
 */

/**
 * Make an API request using XMLHttpRequest (bypasses fetch interceptor).
 * 
 * @param {string} url - URL to request
 * @param {object} options - Request options
 * @returns {Promise<{ok, status, traceId, json, rawText, error}>}
 */
export function xhrFetch(url, options = {}) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    const method = options.method || 'GET';
    
    xhr.open(method, url, true);
    
    // Set headers
    if (options.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        if (value !== undefined) {
          xhr.setRequestHeader(key, value);
        }
      });
    }
    
    // Set timeout (30 seconds default)
    xhr.timeout = options.timeout || 30000;
    
    xhr.onload = () => {
      const traceId = xhr.getResponseHeader('x-trace-id') || null;
      const rawText = xhr.responseText;
      
      let json = null;
      try {
        json = rawText ? JSON.parse(rawText) : null;
      } catch {
        // Not JSON
      }
      
      // Prefer trace_id from body if present
      const finalTraceId = (json && typeof json === 'object' && json.trace_id) || traceId;
      
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        traceId: finalTraceId,
        json,
        rawText,
        error: null
      });
    };
    
    xhr.onerror = () => {
      resolve({
        ok: false,
        status: 0,
        traceId: null,
        json: null,
        rawText: null,
        error: {
          code: 'NETWORK_ERROR',
          message: 'Network request failed'
        }
      });
    };
    
    xhr.ontimeout = () => {
      resolve({
        ok: false,
        status: 0,
        traceId: null,
        json: null,
        rawText: null,
        error: {
          code: 'TIMEOUT',
          message: 'Request timed out'
        }
      });
    };
    
    // Send body
    if (options.body) {
      xhr.send(options.body);
    } else {
      xhr.send();
    }
  });
}

/**
 * Authenticated GET request via XHR.
 */
export function xhrFetchAuth(url, options = {}) {
  const token = localStorage.getItem('accessToken');
  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`
  };
  return xhrFetch(url, { ...options, headers });
}

/**
 * Authenticated JSON POST request via XHR.
 */
export function xhrFetchAuthPost(url, body, options = {}) {
  const token = localStorage.getItem('accessToken');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options.headers
  };
  return xhrFetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    ...options
  });
}


// Re-export helper functions from response.js
export { extractErrorMessage, extractErrorInfo, fetchCustodyChain } from './response';
